import React from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { money, day } from "../utils/planChange";

/**
 * The window before a plan changes, shared by the student and coach plans pages
 * (Evan, Jul 20: the plan and what it costs are confirmed first; Sep 9: what was paid is
 * kept). `change` is {plan, preview} for a paid plan, with the server's preview of
 * what's charged today or when a cheaper plan starts, or {plan, free: true} for the
 * free plan, which is the same as Cancel for someone who pays by card (a renewal the card
 * didn't pay, past_due, ends now: the backend cancels it so Stripe stops retrying it).
 * `freeNow`: the free plan starts right away (a coach whose plan isn't paid by card).
 * Later charges are said by date only: a promo code's discount stays on the subscription
 * (and goes on to the cheaper plan), so Stripe can charge less than the plan's price.
 */
export default function PlanChangeConfirm({ change, membership, paysByCard, freeNow, promoNote, error, busy, onClose, onConfirm, isDarkMode }) {
    const { plan, preview, free } = change;
    const text = `text-sm mb-2 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`;
    const later = free || preview.kind === "downgrade";
    const current = free ? membership?.TierName : preview.currentTierName;
    // The last renewal didn't go through: there's no paid time to keep, so the free plan starts now
    const pastDue = free && paysByCard && membership?.StripeSubscriptionStatus === "past_due";
    const title = free ? `Switch to ${plan.TierName}`
        : `${preview.kind === "upgrade" ? "Upgrade" : preview.kind === "downgrade" ? "Downgrade" : "Switch"} to ${plan.TierName}`;

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 9999 }} onClick={() => !busy && onClose()}>
            <div role="dialog" aria-label={title} className={`rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 ${isDarkMode ? "bg-slate-800" : "bg-white"}`} onClick={(e) => e.stopPropagation()}>
                <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{title}</h3>
                {free && freeNow ? (
                    <p className={text}>
                        {Number(membership?.Price) > 0 ? `${current} ends now and you move to ${plan.TierName}.` : `You move to ${plan.TierName} now.`} Nothing is charged.
                    </p>
                ) : pastDue ? (
                    <p className={text}>
                        Your last payment didn't go through, so {current} ends now and you move to {plan.TierName}. That payment isn't tried again and nothing more is charged.
                    </p>
                ) : free ? (
                    <>
                        <p className={text}>
                            You keep {current} until {day(membership?.EndDate)}, then move to {plan.TierName}.
                            {paysByCard ? " You won't be charged again." : " Nothing is charged."}
                        </p>
                        <p className={text}>Changed your mind? Keep {current} from My Membership before then.</p>
                    </>
                ) : later ? (
                    <>
                        <p className={text}>You keep {current} until {day(preview.effectiveOn)}, then move to {plan.TierName}.</p>
                        <p className={text}>Nothing is charged until then, and you can keep {current} from My Membership before then.</p>
                    </>
                ) : (
                    <>
                        {preview.amountDue > 0 ? (
                            <p className={text}>You pay <span className="font-bold">{money(preview.amountDue)}</span> today: {plan.TierName} from now on, less the unused time on {current}.</p>
                        ) : preview.trialEndsOn ? (
                            <p className={text}>Nothing is charged today: your free trial goes on until {day(preview.trialEndsOn)}.</p>
                        ) : (
                            <p className={text}>Nothing is charged today: the unused time on {current} covers it.</p>
                        )}
                        {preview.credit > 0 && <p className={text}>The {money(preview.credit)} left over goes toward your next payments.</p>}
                        <p className={text}>{preview.trialEndsOn ? `Then ${plan.TierName} is billed from ${day(preview.trialEndsOn)}.` : `Then ${plan.TierName} renews on ${day(preview.renewsOn)}.`}</p>
                    </>
                )}
                {promoNote && <p className={text}>{promoNote}</p>}
                {error && <p className="text-sm font-medium text-error mt-3">{error}</p>}
                <div className="flex flex-col-reverse sm:flex-row gap-3 mt-5">
                    {/* (side by side from a tablet up; stacked full width on a phone) */}
                    <Button variant="secondary" className="w-full sm:flex-1" onClick={onClose} disabled={busy}>Back</Button>
                    <Button variant="primary" className="w-full sm:flex-1" loading={busy} onClick={onConfirm}>
                        {free ? "Yes, switch" : later ? "Downgrade" : preview.amountDue > 0 ? `Pay ${money(preview.amountDue)} now` : preview.kind === "switch" ? "Switch now" : "Upgrade now"}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
}
